import { SuggestionCell, toCellDisplay } from '@ayocore/platform/client/components/EditableGrid';
import type {
  EditableGridColumn,
  EditableGridSuggestOption,
} from '@ayocore/platform/client/components/EditableGrid';
import { partnerRepository } from '@ayocore/erp/crm/client/rpc';
import type { SupplierNameSource, SupplierQuoteColumnKey, SupplierQuotePatch } from './types';

type SupplierSearchRow = {
  id?: number;
  name?: string | null;
  full_name?: string | null;
  inn?: string | null;
};

function getSupplierName(quote: SupplierNameSource): string {
  return quote.supplier_name ?? quote.supplier?.name ?? quote.supplier?.full_name ?? '';
}

function mapSupplierRowsToSuggestOptions(rows: SupplierSearchRow[]): EditableGridSuggestOption[] {
  return (rows ?? [])
    .filter(row => row.id != null)
    .map(row => ({
      id: String(row.id),
      label: row.name ?? row.full_name ?? `ID ${row.id}`,
      hint: row.inn ?? undefined,
      payload: row,
    }));
}

async function searchSuppliers(query: string): Promise<EditableGridSuggestOption[]> {
  const rows = (await partnerRepository.search(query).limit(8).fetch()) as SupplierSearchRow[];
  return mapSupplierRowsToSuggestOptions(rows ?? []);
}

/**
 * Creates a supplier suggestion column.
 * Generic over Row to support DealRowQuote, LeadRowQuote, etc.
 * Row must extend SupplierNameSource (supplier_id, supplier_name, supplier).
 */
export function createQuoteSupplierColumn<Row extends SupplierNameSource>(): EditableGridColumn<
  Row,
  SupplierQuotePatch,
  SupplierQuoteColumnKey
> {
  return SuggestionCell<SupplierNameSource, SupplierQuotePatch, SupplierQuoteColumnKey>({
    key: 'supplier',
    title: 'Поставщик',
    width: 220,
    getDisplayFromRow: row =>
      toCellDisplay(getSupplierName(row), { linked: row.supplier_id != null }),
    getEditorFromRow: row => ({
      value: getSupplierName(row),
      meta: { selectedRef: row.supplier_id ?? null },
    }),
    getSelectedRefFromRow: row => row.supplier_id ?? null,
    onSearch: searchSuppliers,
    onPatch: ({ row, value, selectedRef }) => {
      const typedName = value.trim();
      const nextSupplierId = typeof selectedRef === 'number' ? selectedRef : null;
      const nextSupplierName = nextSupplierId != null ? null : typedName || null;
      const prevSupplierId = row.supplier_id ?? null;
      const prevSupplierName = row.supplier_name ?? null;

      return {
        hasChanges: nextSupplierId !== prevSupplierId || nextSupplierName !== prevSupplierName,
        apiPatch: {
          supplier_id: nextSupplierId,
          supplier_name: nextSupplierName,
        },
        localPatch: {
          supplier_id: nextSupplierId,
          supplier_name: nextSupplierId != null ? typedName || null : nextSupplierName,
          supplier: null,
        },
      };
    },
  }) as EditableGridColumn<Row, SupplierQuotePatch, SupplierQuoteColumnKey>;
}
